// Shared "Customer Profile Synopsis" dialog (Total Customers + Customers Synopsis).
// The eye buttons open the individual customer report pages.
import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Paper, Grid, Button, Card, CardContent, Chip, IconButton, Tooltip,
  CircularProgress, Divider, Dialog, DialogTitle, DialogContent, DialogActions,
  FormControl, InputLabel, Select, MenuItem,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PersonIcon from '@mui/icons-material/Person';
import StarsIcon from '@mui/icons-material/Stars';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../constants/constants';
import { customerNoOf } from './CustomerReportShell';

const maskMobile = (mobile, canViewSensitive) => {
  if (!mobile || mobile === 'N/A') return 'N/A';
  if (canViewSensitive) return mobile;
  if (mobile.length <= 4) return mobile;
  return mobile.slice(0, 4) + '****' + mobile.slice(-2);
};

const maskEmail = (email, canViewSensitive) => {
  if (!email || email === 'N/A') return 'N/A';
  if (canViewSensitive) return email;
  const parts = email.split('@');
  if (parts.length < 2) return email.slice(0, 3) + '****';
  const name = parts[0];
  const maskedName = name.length <= 3 ? name[0] + '***' : name.slice(0, 3) + '****';
  return `${maskedName}@${parts[1]}`;
};

const maskVPA = (vpa, canViewSensitive) => {
  if (!vpa || vpa === 'N/A') return 'N/A';
  if (canViewSensitive) return vpa;
  const parts = vpa.split('@');
  if (parts.length < 2) return vpa.slice(0, 3) + '****';
  const name = parts[0];
  const maskedName = name.length <= 3 ? name[0] + '***' : name.slice(0, 3) + '****';
  return `${maskedName}@${parts[1]}`;
};

const EMPTY = {
  transactions: [], games: [], referrals: [], smartPoints: [], availableSmartPoints: 0,
  withdrawals: [], welfare: [], availableWelfareAmount: 0, totalsByMode: null,
};

const CustomerProfileDialog = ({ open, onClose, customer: customerProp, canViewSensitive }) => {
  const navigate = useNavigate();
  const [customer, setCustomer] = useState(customerProp);
  const [data, setData] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [vpaFilter, setVpaFilter] = useState('all');

  const cId = customerProp && (customerProp.customerId || customerProp._id);

  useEffect(() => {
    if (!open || !cId) return undefined;
    let cancelled = false;
    setCustomer(customerProp);
    setVpaFilter('all');
    setData(EMPTY);
    setLoading(true);
    axios.get(`${API_BASE_URL}/customerProfiles/${cId}/synopsis`)
      .then((res) => {
        if (cancelled || !res.data) return;
        const d = res.data;
        if (d.customer) setCustomer(d.customer);
        setData({
          transactions: d.transactions || [],
          games: d.games || [],
          referrals: d.referrals || [],
          smartPoints: d.smartPoints || [],
          availableSmartPoints: d.availableSmartPoints || 0,
          withdrawals: d.withdrawals || [],
          welfare: d.welfare || [],
          availableWelfareAmount: d.availableWelfareAmount || 0,
          totalsByMode: d.totalsByMode || null,
        });
      })
      .catch((err) => console.error('Error fetching customer synopsis:', err))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, cId]);

  const go = (path) => {
    onClose();
    navigate(`/customer-report/${path}/${cId}`);
  };

  const c = customer || {};
  const nameParts = (c.customerName || '').trim().split(' ');
  const firstName = c.firstName || nameParts[0] || 'N/A';
  const lastName = c.lastName || nameParts.slice(1).join(' ') || 'N/A';
  const district = (c.customerDistrictName && c.customerDistrictName !== 'N/A') ? c.customerDistrictName : 'N/A';
  const constituency = c.constituency || c.customerConstituency || c.customerTalukName || 'N/A';
  const status = String(c.customerStatus || c.status || 'active').toLowerCase();

  const rawVpas = Array.isArray(c.customerVPA)
    ? c.customerVPA.map((v) => (typeof v === 'string' ? v : v.vpa || '')).filter(Boolean)
    : [];

  const matchesVpa = (item) => {
    if (vpaFilter === 'all') return true;
    const vpa = (item.vpaId || item.payerVPA || item.vpa || item.customerVPA || '').toLowerCase();
    return vpa.includes(vpaFilter.toLowerCase());
  };

  const filteredTx = data.transactions.filter(matchesVpa);
  const filteredGames = data.games.filter(matchesVpa);
  const filteredSmart = data.smartPoints.filter(matchesVpa);
  const filteredWithdrawals = data.withdrawals.filter(matchesVpa);
  const filteredWelfare = data.welfare.filter(matchesVpa);

  const smartPoints = vpaFilter === 'all'
    ? (data.availableSmartPoints || 0)
    : filteredSmart.reduce((a, s) => a + (Number(s.amount) || Number(s.points) || 0), 0);
  const welfareAmount = vpaFilter === 'all'
    ? (data.availableWelfareAmount || 0)
    : filteredWelfare.reduce((a, w) => a + (Number(w.amount) || 0), 0);
  // only money that really left the balance counts: pending / processing / failed requests are not withdrawn yet
  const isPaidOut = (w) => !w.status || w.status === 'completed';
  const withdrawalTotal = filteredWithdrawals.filter(isPaidOut).reduce((a, w) => a + (Number(w.amount) || 0), 0);
  const withdrawalPending = filteredWithdrawals.filter((w) => w.status === 'pending' || w.status === 'processing').length;
  const txTotals = (vpaFilter === 'all' && data.totalsByMode) || null;
  const upiCount = txTotals ? (txTotals.UPI?.count || 0) : filteredTx.filter((t) => String(t.paymentMode || '').toUpperCase() !== 'CASH').length;
  const cashCount = txTotals ? (txTotals.CASH?.count || 0) : filteredTx.filter((t) => String(t.paymentMode || '').toUpperCase() === 'CASH').length;

  const tiles = [
    { label: 'Show Customer Transaction', value: `${filteredTx.length} Recorded`, note: `UPI ${upiCount} · Cash ${cashCount}`, tip: 'View Full Transaction History', path: 'transactions' },
    { label: 'Show Customer Game Card', value: `${filteredGames.length} Played`, tip: 'View Gaming History', path: 'games' },
    { label: 'Show Customer Referrals', value: `${data.referrals.length} Referrals`, tip: 'View Referral Details', path: 'referral-rewards' },
    { label: 'Show Customer Smart Points', value: `${smartPoints.toLocaleString('en-IN')} Total Points`, tip: 'View Smart Points History', path: 'balance' },
    { label: 'Show Customer Withdrawals', value: `₹${withdrawalTotal.toLocaleString('en-IN')} Withdrawn`, note: withdrawalPending ? `${withdrawalPending} pending` : undefined, tip: 'View Withdrawal Requests', path: 'withdrawals' },
    { label: 'Show Customer Welfare History', value: `₹${welfareAmount.toLocaleString('en-IN')} Available`, tip: 'View Welfare Details', path: 'welfare-transactions' },
  ];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ fontWeight: 700, backgroundColor: '#1e293b', color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        Customer Profile Synopsis
        <Button size="small" onClick={onClose} sx={{ color: 'white' }}>Close</Button>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 4, backgroundColor: '#fafafa' }}>
        {!customerProp ? null : (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              <PersonIcon color="primary" sx={{ fontSize: 40 }} />
              <Typography variant="h5" sx={{ fontWeight: 'bold' }}>Customer Profile Summary</Typography>
              <Chip
                label={status === 'active' ? 'ACTIVE' : (status === 'deactive' ? 'INACTIVE' : status.toUpperCase())}
                color={status === 'active' ? 'success' : 'error'}
                size="small"
                sx={{ ml: 'auto' }}
              />
            </Box>

            <Divider sx={{ mb: 3 }} />

            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="caption" color="textSecondary">Show Customer ID</Typography>
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'primary.main' }}>{customerNoOf(c)}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="caption" color="textSecondary">Show Customer First Name</Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>{firstName}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="caption" color="textSecondary">Show Customer Last Name</Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>{lastName}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="caption" color="textSecondary">Show Customer Mobile Number {!canViewSensitive && '(Hashed)'}</Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>{maskMobile(c.customerMobile, canViewSensitive)}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="caption" color="textSecondary">Show Customer Email {!canViewSensitive && '(Hashed)'}</Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>{maskEmail(c.customerEmail, canViewSensitive)}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="caption" color="textSecondary">Show Customer STATE</Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>{c.customerState || c.stateCode || 'Kerala'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="caption" color="textSecondary">Show Customer DISTRICT</Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>{district}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="caption" color="textSecondary">Customer Constituency</Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, color: '#1e293b' }}>{constituency}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="caption" color="textSecondary">Show Customer Current Pincode</Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>{c.customerPincode || 'N/A'}</Typography>
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <Paper elevation={1} sx={{ p: 2, borderRadius: 2, backgroundColor: '#f0f9ff', border: '1px solid #bae6fd' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#0369a1' }}>Customer Available Smart Points</Typography>
                    <StarsIcon sx={{ color: '#0284c7', fontSize: 20 }} />
                  </Box>
                  <Typography variant="h5" sx={{ fontWeight: 800, color: '#0284c7', mb: 1 }}>
                    {loading ? '...' : `${smartPoints.toLocaleString('en-IN')} Pts`}
                  </Typography>
                  <FormControl fullWidth size="small">
                    <InputLabel id="profile-vpa-filter-label" sx={{ fontSize: '0.8rem' }}>Filter by VPA ID</InputLabel>
                    <Select
                      labelId="profile-vpa-filter-label"
                      size="small"
                      value={vpaFilter}
                      label="Filter by VPA ID"
                      onChange={(e) => setVpaFilter(e.target.value)}
                      sx={{ backgroundColor: 'white', height: 34, fontSize: '0.85rem' }}
                    >
                      <MenuItem value="all">All VPA IDs (Combined)</MenuItem>
                      {rawVpas.map((vpa, idx) => (
                        <MenuItem key={idx} value={vpa}>{maskVPA(vpa, canViewSensitive)}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Paper>
              </Grid>

              <Grid item xs={12} sm={12} md={8}>
                <Typography variant="caption" color="textSecondary">Show List of Customer VPA ID {!canViewSensitive && '(Hashed)'}</Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 0.5 }}>
                  {rawVpas.length > 0 ? (
                    rawVpas.map((v, i) => <Chip key={i} label={maskVPA(v, canViewSensitive)} size="small" color="primary" variant="outlined" />)
                  ) : (
                    <Typography variant="body2" color="textSecondary">No VPAs registered</Typography>
                  )}
                </Box>
              </Grid>
            </Grid>

            <Divider sx={{ my: 4 }} />

            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 3, color: 'secondary.main' }}>
              Customer Activity Synopsis & Details
            </Typography>

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress /></Box>
            ) : (
              <Grid container spacing={3}>
                {tiles.map((t) => (
                  <Grid item xs={12} sm={6} md={4} key={t.path}>
                    <Card elevation={2} sx={{ p: 1, borderRadius: 2 }}>
                      <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box>
                          <Typography variant="caption" color="textSecondary">{t.label}</Typography>
                          <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>{t.value}</Typography>
                          {t.note && <Typography variant="caption" color="text.secondary">{t.note}</Typography>}
                        </Box>
                        <Tooltip title={t.tip}>
                          <IconButton color="primary" onClick={() => go(t.path)}>
                            <VisibilityIcon />
                          </IconButton>
                        </Tooltip>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button variant="contained" onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default CustomerProfileDialog;
